import { isWeb, breakpoints } from './platform';
import { useResponsive } from './useResponsive';

/**
 * Which navigation shell owns the current viewport.
 *
 * WHY THIS EXISTS. Navigation availability must follow the ACTIVE LAYOUT, never
 * `isWeb`. `isWeb` is a module constant (`platform.ts:8`) evaluated at import
 * time — it cannot express width, so every width decision made with it is wrong
 * at one end of the range. That is exactly how phone web lost its navigation:
 * all three role navigators hid the tab bar for EVERY web width while the
 * desktop shell only mounts at >= 768px, leaving a dead zone below it with no
 * tab bar, no sidebar, and (because ~37 screens suppressed their own header
 * under `{!isWeb && …}`) frequently no page title either.
 *
 * Exactly one of these is true at a time, and together they cover every case.
 */

/**
 * True when the viewport is phone-shaped and the TAB BAR is the navigation
 * owner: always on native, and on web only below the tablet breakpoint.
 *
 * HYDRATION. `useResponsive` reports `width: 0` until its mount effect runs, so
 * on web this is false during the server-rendered pass — identical to the markup
 * the app produces today. The tab bar can therefore only ever appear AFTER mount
 * and only on a phone-width viewport; a desktop visitor never sees it flash.
 * On native the `!isWeb` arm short-circuits, so pre-mount width never matters.
 */
export function usePhoneShell(): boolean {
  const { width } = useResponsive();
  if (!isWeb) return true;
  return width > 0 && width < breakpoints.md;
}

/**
 * True when the web sidebar shell (`WebAppShell`) is mounted and owns navigation,
 * page title and breadcrumbs. Mirrors `AppLayout`'s own condition so the two can
 * never disagree about who is drawing the chrome.
 */
export function useDesktopShell(): boolean {
  const { isDesktop, isTablet } = useResponsive();
  return isWeb && (isDesktop || isTablet);
}
